<template>
  <form @submit.prevent="$emit('submit')">
    <div class="space-y-6 pb-12">
      <!-- Dane z MedfileAPI -->
      <div>
        <h2 class="mb-3 px-1 text-sm font-semibold text-gray-900">
          Dane z MedfileAPI
        </h2>
        <div class="overflow-hidden rounded-2xl bg-white shadow-sm ring-1 ring-gray-950/5">
          <section class="px-6 py-8 sm:px-8">
            <div class="medfile-synced-fields opacity-60">
              <text-input
                :model-value="loc.remote_name || ''"
                class="w-full"
                :label="`Nazwa (${activeEditingLocale.toUpperCase()})`"
                readonly
                tabindex="-1"
              />
            </div>
          </section>
        </div>
      </div>

      <!-- Dane lokalne -->
      <div>
        <h2 class="mb-3 px-1 text-sm font-semibold text-gray-900">
          Dane lokalne
        </h2>
        <div class="overflow-hidden rounded-2xl bg-white shadow-sm ring-1 ring-gray-950/5">
          <div class="divide-y divide-gray-100">
            <!-- Nazwa lokalna -->
            <section class="px-6 py-8 sm:px-8">
              <div class="mb-5 flex flex-wrap items-center justify-between gap-4">
                <h2 class="text-[11px] font-semibold uppercase tracking-wider text-gray-400">
                  Nazwa lokalna ({{ activeEditingLocale.toUpperCase() }})
                </h2>
                <toggle-switch
                  v-model="form.specialisation.use_local_name"
                  :errors="form.errors['specialisation.use_local_name'] || []"
                  description="Używaj nazwy lokalnej"
                />
              </div>
              <text-input
                v-model="loc.name"
                :errors="localeErrors('name')"
                class="w-full"
                :required="activeEditingLocale === 'pl'"
                maxlength="255"
              />
              <p class="mt-2 text-xs text-gray-400">
                Gdy przełącznik jest włączony, wszędzie wyświetlana jest ta nazwa (PL/EN).
                W przeciwnym razie pokazujemy nazwę z MedfileAPI.
              </p>
            </section>

            <!-- Opis -->
            <section class="px-6 py-8 sm:px-8">
              <h2 class="mb-5 text-[11px] font-semibold uppercase tracking-wider text-gray-400">
                Opis ({{ activeEditingLocale.toUpperCase() }})
              </h2>
              <textarea-input
                v-model="loc.description"
                :errors="localeErrors('description')"
                class="w-full"
                rows="5"
                maxlength="5000"
              />
            </section>

            <!-- Witryna -->
            <section class="px-6 py-8 sm:px-8">
              <h2 class="mb-5 text-[11px] font-semibold uppercase tracking-wider text-gray-400">
                Witryna
              </h2>
              <site-context-select
                v-model="form.specialisation.site_context"
                :errors="form.errors['specialisation.site_context'] || []"
                :options="siteContextOptions"
                class="w-full sm:max-w-xs"
                required
              />
            </section>

            <!-- FAQ -->
            <section class="px-6 py-8 sm:px-8">
              <h2 class="mb-5 text-[11px] font-semibold uppercase tracking-wider text-gray-400">
                FAQ ({{ activeEditingLocale.toUpperCase() }})
              </h2>
              <div class="space-y-5">
                <div
                  v-for="(item, index) in loc.faq"
                  :key="index"
                  class="rounded-xl bg-gray-50/80 p-4 sm:p-5"
                >
                  <div class="mb-4 flex items-center justify-between gap-2">
                    <span class="text-xs font-semibold uppercase tracking-wider text-gray-400">
                      Pytanie {{ index + 1 }}
                    </span>
                    <button
                      type="button"
                      class="inline-flex shrink-0 items-center justify-center rounded-lg p-1.5 text-gray-400 transition hover:bg-red-50 hover:text-red-600"
                      title="Usuń"
                      @click="removeFaqItem(index)"
                    >
                      <icon name="trash" class="size-5 stroke-current" />
                    </button>
                  </div>
                  <text-input
                    v-model="item.question"
                    class="w-full pb-4"
                    label="Pytanie"
                    maxlength="500"
                  />
                  <textarea-input
                    v-model="item.answer"
                    class="w-full"
                    label="Odpowiedź"
                    rows="3"
                    maxlength="5000"
                  />
                </div>
                <button
                  type="button"
                  class="inline-flex items-center gap-1.5 text-sm font-medium text-indigo-600 transition hover:text-indigo-700"
                  @click="addFaqItem"
                >
                  <span aria-hidden="true" class="text-base leading-none">+</span>
                  Dodaj pytanie
                </button>
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>
    <slot />
  </form>
</template>

<script>
import TextInput from '@/Shared/TextInput.vue';
import TextareaInput from '@/Shared/TextareaInput.vue';
import SiteContextSelect from '@/Shared/SiteContextSelect.vue';
import ToggleSwitch from '@/Shared/ToggleSwitch.vue';
import Icon from '@/Shared/Icon.vue';

export default {
  components: {
    TextInput,
    TextareaInput,
    SiteContextSelect,
    ToggleSwitch,
    Icon,
  },
  props: {
    modelValue: {
      type: Object,
      required: true,
    },
    siteContextOptions: {
      type: Array,
      default: () => [],
    },
    editingLocale: {
      type: String,
      default: undefined,
    },
    showActiveToggle: {
      type: Boolean,
      default: true,
    },
    lockSyncedFields: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['update:modelValue', 'update:editingLocale', 'submit'],
  data() {
    return {
      internalEditingLocale: 'pl',
    };
  },
  computed: {
    form: {
      get() {
        return this.modelValue;
      },
      set(val) {
        this.$emit('update:modelValue', val);
      },
    },
    activeEditingLocale: {
      get() {
        return this.editingLocale !== undefined ? this.editingLocale : this.internalEditingLocale;
      },
      set(val) {
        if (this.editingLocale !== undefined) {
          this.$emit('update:editingLocale', val);
        } else {
          this.internalEditingLocale = val;
        }
      },
    },
    loc() {
      const slice = this.form.specialisation[`data_${this.activeEditingLocale}`];
      if (!Array.isArray(slice.faq)) {
        slice.faq = [];
      }
      return slice;
    },
  },
  created() {
    if (typeof this.form.specialisation.use_local_name !== 'boolean') {
      this.form.specialisation.use_local_name = Boolean(this.form.specialisation.use_local_name);
    }
    ['data_pl', 'data_en'].forEach((key) => {
      if (!this.form.specialisation[key]) {
        this.form.specialisation[key] = { name: '', remote_name: '', description: '', faq: [] };
      }
      if (typeof this.form.specialisation[key].remote_name !== 'string') {
        this.form.specialisation[key].remote_name = this.form.specialisation[key].remote_name || '';
      }
      if (!Array.isArray(this.form.specialisation[key].faq)) {
        this.form.specialisation[key].faq = [];
      }
    });
  },
  methods: {
    addFaqItem() {
      this.loc.faq.push({ question: '', answer: '' });
    },
    removeFaqItem(index) {
      this.loc.faq.splice(index, 1);
    },
    localeErrors(field) {
      const errors = this.form.errors || {};
      const raw =
        errors[`specialisation.data_${this.activeEditingLocale}.${field}`] ||
        errors[`specialisation.${field}`] ||
        errors[field];
      if (!raw) return [];
      return Array.isArray(raw) ? raw : [raw];
    },
  },
};
</script>

<style scoped>
.medfile-synced-fields :deep(.form-label) {
  color: var(--color-green-700);
}

.medfile-synced-fields :deep(.form-input),
.medfile-synced-fields :deep(.form-textarea) {
  cursor: default;
  border-color: var(--color-green-200);
  background-color: var(--color-green-50);
  color: var(--color-green-800);
}

.medfile-synced-fields :deep(.form-input:focus),
.medfile-synced-fields :deep(.form-textarea:focus) {
  border-color: var(--color-green-300);
  outline: none;
  box-shadow: none;
}
</style>
